/**
 * Etapa 6 - Funis: liga carrinho abandonado e expirados, mostrando a regua
 * padrao de cada um.
 *
 * A regua e LISTADA, nao editada: os toques sao ajustados depois, no CRM da
 * empresa. Expirados sem oferta recorrente vira aviso, nao bloqueio -- a
 * equipe pode cadastrar a assinatura depois; o que nao pode e a tela fingir
 * que o funil vai ter o que recuperar.
 *
 * Principio do dono: funil SO DEPOIS DO FATO. Cada cartao mostra o fato que
 * dispara a regua (checkout abandonado ha 30 min, assinatura ja vencida); nao
 * existe lembrete antes de vencer nem cobranca preventiva -- sem o fato nao ha
 * recuperacao e nao ha comissao.
 */

import { Badge, Footnote, Icon, Panel, Toggle } from '../../../../components/ui';
import type { FunnelKind } from '../../../../types';
import { ordenarFunis, temOfertaRecorrente } from '../../assistente';
import { FUNIL_INFO, FUNIS } from '../../vocabulario';
import { erroDe, type EtapaProps } from './props';

export function EtapaFunis(props: EtapaProps): JSX.Element {
  const { form, onChange } = props;
  const erro = erroDe(props, 'funis');

  const alternar = (kind: FunnelKind, ligado: boolean): void => {
    const lista = ligado ? [...form.funis, kind] : form.funis.filter((f) => f !== kind);
    onChange({ funis: ordenarFunis(lista) });
  };

  return (
    <div className="flex flex-col gap-4">
      <ul className="grid gap-4 md:grid-cols-2">
        {FUNIS.map((kind) => {
          const info = FUNIL_INFO[kind];
          const ligado = form.funis.includes(kind);
          const avisoRecorrente = kind === 'expirados' && ligado && !temOfertaRecorrente(form);
          return (
            <li key={kind}>
              <Panel tone={ligado ? 'surface' : 'elevated'} className={`flex h-full flex-col gap-4 p-5 ${ligado ? '' : 'border-dashed border-edge'}`}>
                <Toggle
                  checked={ligado}
                  onChange={(proximo) => alternar(kind, proximo)}
                  label={
                    <span className="flex items-center gap-2">
                      <Icon name={info.icon} size="sm" />
                      {info.label}
                    </span>
                  }
                  description={info.descricao}
                />
                <p className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                  <Badge tone={ligado ? 'brand' : 'neutral'} icon="escudo">
                    Dispara em: {info.fato}
                  </Badge>
                </p>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Régua padrão, contada a partir do fato</p>
                  <ol className="mt-1.5 flex flex-col gap-1 text-sm text-ink-muted">
                    {info.regua.map((linha, i) => (
                      <li key={linha} className="flex items-start gap-2">
                        <span aria-hidden="true" className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-semibold ${ligado ? 'bg-brand/10 text-brand' : 'bg-ink/5 text-ink-subtle'}`}>
                          {i + 1}
                        </span>
                        <span className={ligado ? 'text-ink' : ''}>{linha}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                {avisoRecorrente ? (
                  <p className="flex items-start gap-2 text-xs text-ink-muted">
                    <Icon name="alerta" size="xs" className="mt-0.5 shrink-0 text-status-warning" />
                    <span>Nenhuma oferta recorrente foi cadastrada na etapa 4. Este funil só terá o que recuperar quando houver uma assinatura.</span>
                  </p>
                ) : null}
              </Panel>
            </li>
          );
        })}
      </ul>
      {erro !== null ? (
        <p role="alert" className="text-xs text-status-critical">
          {erro}
        </p>
      ) : null}
      <Footnote>
        Nenhum toque acontece antes do fato: o carrinho só entra depois de 30 minutos sem pagar e o expirado só depois de vencer. A espera de 30 minutos e o teto de 2
        ligações por lead são fixos (RF04 e RF08). Textos e horários dos toques são ajustados depois, no CRM da empresa.
      </Footnote>
    </div>
  );
}
